import type { ICreditBundle } from "./organizations";

export const SITE = "OpenSanctions"
export const BASE_URL = process.env.BASE_URL || "https://www.opensanctions.org"
export const INDEX_URL = "https://data.opensanctions.org/datasets/latest/index.json"
// Bulk list of sanctioned targets, used to seed the entity sitemap. One row per
// entity with an `id` and `last_change`; ~70k rows / ~60MB (see lib/sitemap.ts).
export const SANCTIONS_TARGETS_URL = "https://data.opensanctions.org/datasets/latest/sanctions/targets.simple.csv"
export const MODEL_URL = "https://data.opensanctions.org/meta/model.json"
export const PROGRAMS_URL = 'https://data.opensanctions.org/meta/programs.json'
export const LICENSE_URL = "https://creativecommons.org/licenses/by-nc/4.0/"
export const CMS_URL = "https://opensanctions.directus.app"
export const MAIN_DATASET = "default"
export const KYB_DATASET = "kyb"
export const SEARCH_SCHEMA = "Thing"
// Approximate number of individual facts (statements) in the default
// collection, for headline copy. Hand-maintained until the data pipeline
// exports a live statement count we can read like the entity/source figures.
export const STATEMENT_COUNT = 47_000_000
// The API credit bundles on public offer, as shown on /api/. The portal sells
// signed, per-organization offers from the API instead; this list only needs
// to match the published price sheet. Amounts are in cents.
export const CREDIT_BUNDLES: ICreditBundle[] = [
  { credits: 500, cost_amount: 5000, cost_currency: 'EUR', expiry_days: 365 },
  { credits: 5000, cost_amount: 40000, cost_currency: 'EUR', expiry_days: 365 },
  { credits: 30000, cost_amount: 200000, cost_currency: 'EUR', expiry_days: 365 },
  { credits: 100000, cost_amount: 500000, cost_currency: 'EUR', expiry_days: 365 },
  { credits: 500000, cost_amount: 1500000, cost_currency: 'EUR', expiry_days: 365 },
]
export const DEFAULT_ALGORITHM = "logic-v2"
// Older scoring methods we keep documenting but don't want to promote: hidden
// from the advanced search UI and grouped under "Deprecated" on the matcher page.
export const DEPRECATED_ALGORITHMS = ["regression-v1", "name-based", "name-qualified"]
export const CLAIM = "Supreme Data on Supreme Leaders"
// Search-facing variant of CLAIM: used where machines read the tagline (the
// homepage <title>, JSON-LD descriptions) while CLAIM stays on human surfaces
// (hero, footer). Keep it short: "OpenSanctions: " + SEO_CLAIM is the SERP title.
export const SEO_CLAIM = "The open database of sanctions, PEPs and watchlists"
export const SUBCLAIM = "The open-source database of sanctions, watchlists, and politically exposed persons — aggregating hundreds of sources and relied on by compliance teams, investigators, and journalists."
export const FEATURED_COLLECTIONS = ['default', 'sanctions'];
export const EXTRA_COLLECTIONS = ['peps', 'securities', 'maritime', 'kyb', 'openownership', 'crime'];
export const ARTICLE_INDEX_SUMMARY = "Updates from OpenSanctions, including new features, technical deep dives, and analysis."
export const GA_TRACKING_ID = 'G-YRZENQXNNR';
export const REVALIDATE_SHORT = 300;
export const REVALIDATE_BASE = 3600 * 2;
// Per-entity API responses: high-cardinality (millions of possible keys, mostly
// crawler-driven), so a shorter window bounds their share of the Redis cache.
export const REVALIDATE_ENTITY = 3600;
export const REVALIDATE_LONG = REVALIDATE_BASE * 2;
// Flag artwork is near-immutable; a long TTL seeds the CDN and browsers with
// stable copies. Redeploying the site refreshes the CDN via cache miss on the
// new revision only after expiry — acceptable for cosmetic assets.
export const REVALIDATE_FLAGS = 14 * 24 * 3600;
export const FETCH_CONCURRENCY = 16;
export const THEME_COLOR = '#2563eb';
export const SOCIAL_IMAGE_URL = "https://assets.opensanctions.org/images/nura/social.png";
export const LOGO_URL = "https://assets.opensanctions.org/images/nura/logo-icon-color.png";
export const ARTICLE_DEFAULT_IMAGE = "bb87de0a-1bc2-459b-a483-7f2d80f8273c";
export const RSS_MIME = "application/rss+xml";
export const MARKDOWN_MIME = "text/markdown";
// Sections whose pages also exist as raw markdown; server.ts repeats the list.
export const MARKDOWN_SECTIONS = ['/docs/', '/articles/', '/changelog/'];
// Build-baked content; every prod deploy purges the CDN, so a long edge TTL is safe.
export const CONTENT_CACHE_CONTROL = "public, max-age=3600, s-maxage=86400, stale-while-revalidate=86400";
export const MAX_FILTERS_PER_FACET = 3;
export const MAX_FILTERS = 5;

export const API_TOKEN = process.env.API_TOKEN
export const SECRET_KEY = process.env.SECRET_KEY

// Mailgun transactional email (server-only), used by lib/mailer.ts for the
// sales inquiry form. The sending key is a secret; the rest have sensible
// defaults. EU region because the account is EU-hosted. Unlike the Stripe key
// these do NOT fail-fast at import — the mailer validates lazily so the site
// still builds without them (see lib/mailer.ts).
export const MAILGUN_SENDING_KEY = process.env.MAILGUN_SENDING_KEY
export const MAILGUN_DOMAIN = process.env.MAILGUN_DOMAIN || "mailer.opensanctions.org"
export const MAILGUN_API_BASE = process.env.MAILGUN_API_BASE || "https://api.eu.mailgun.net"
export const MAIL_FROM = process.env.MAIL_FROM || `OpenSanctions <noreply@${MAILGUN_DOMAIN}>`
export const SALES_INQUIRY_TO = process.env.SALES_INQUIRY_TO || "sales@opensanctions.org"

// HubSpot portal (public identifier, visible in every embed snippet).
export const HUBSPOT_PORTAL_ID = process.env.HUBSPOT_PORTAL_ID || "26189152"
// Help-desk-connected form behind the /support/ contact form; submissions
// create the ticket and contact in HubSpot (see lib/hubspot.ts).
export const HUBSPOT_SUPPORT_FORM_GUID =
  process.env.HUBSPOT_SUPPORT_FORM_GUID || "d20e1d5a-610c-40aa-bf31-ea9f62fe5fc2"

// client-side variables
export const API_URL = process.env.NEXT_PUBLIC_API_URL || "https://api.opensanctions.org"
export const COOKIE_NAME = process.env.NEXT_PUBLIC_COOKIE_NAME || "os_site_token"

// Ory project holding the user identities. Used to link to the right project
// from the admin interface.
export const ORY_PROJECT_ID =
  process.env.NEXT_PUBLIC_ORY_PROJECT_ID || "a43b34d3-81fc-4d34-9a8f-df47dc76a155"

export const CHARGEBEE_SITE = process.env.NEXT_PUBLIC_CHARGEBEE_SITE || "opensanctions"

export const STRIPE_PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string
if (!STRIPE_PUBLISHABLE_KEY) {
  throw new Error("Missing STRIPE_PUBLISHABLE_KEY")
}

// fake up a semantic ordering of collections
// Risk topics split into two visual tiers for badge styling (see TopicsList).
// The two arrays are a presentation decision local to the site — followthemoney
// only knows a flat `RISKS`/`target_topics` set. Anything risk-bearing that is
// in neither list (role.pep, role.rca, role.oligarch, poi, …) renders as a
// muted "role" descriptor, so the enforcement signal isn't drowned out.

// Enforcement: a state actor has acted against the entity — designation,
// control restriction, criminal status. These borrow the brand's danger red.
export const HIGH_RISK_TOPICS = [
  'sanction',
  'sanction.linked',
  'sanction.control',
  'sanction.counter',
  'export.control',
  'export.control.linked',
  'invest.ban',
  'wanted',
  'crime',
  'crime.fraud',
  'crime.fin',
  'crime.theft',
  'crime.war',
  'crime.boss',
  'crime.terror',
  'crime.traffick',
];

// Regulatory: enhanced-scrutiny and exclusion flags from regulators and
// supervisory bodies — a step below enforcement. These carry the amber tier.
export const REGULATORY_TOPICS = [
  'role.pep',
  'role.rca',
  'corp.disqual',
  'debarment',
  'reg.action',
  'reg.warn',
  'invest.risk',
  'export.risk',
  'mare.detained',
  'mare.shadow',
];

export const SPACER = " · ";

export const BLOCKED_ENTITIES = [
  'ofac-9591',
  'gb-coh-ydbwjewipbc3ibij62kywaoyfm4',
  'addr-bf1f426e322e664bbc9f783fe0c277675c7c51cd',
  'gb-coh-uiboqdf8mfm6bnau27gi0qspgla',
  'addr-049628bc181af349e9c3b677c654322a920eecb6',
  'Q1045488',
];
export const ENTITY_WARNINGS: { [name: string]: string } = {
  'Q4230633': 'The contents of this page have been modified in response to a legal threat on behalf of Mr. Kondrashev.',
  'ru-inn-770402098952': 'Mr. Chubar [resigned from the Management Board of the Credit Bank of Moscow on 10. June 2022](https://mkb.ru/en/news/56243). The information on this page is outdated, reflecting the pre-war state of the Russian companies\' registry.',
  'Q94927016': 'Mr. Casimiro resigned his role as vice president of Rosneft in May 2022. Some of the descriptions on this page sourced from government sources are outdated in describing his relationship with Rosneft as ongoing.',
  'NK-4qiLgZbYdUE66FKEWb64st': "Following communications from Pomegranate Investment AB, the following statement has been added to this page:\n\n*\"Pomegranate Investment AB (publ) is a public investment company established in 2014, with a focus on emerging markets. The company maintains a comprehensive Trade Controls Policy and receives continuous advice from leading international law firms regarding compliance with all applicable sanctions regimes. Pomegranate does not appear on any sanctions lists, has never been subject to sanctions-related investigations, and conducts regular internal compliance reviews. Inclusion in the UANI Iran Business Registry relates solely to transparent business activities in Iran during the JCPOA era.\"*\n\nThe statement has not been verified by OpenSanctions and is included without legal obligation to do so.",
  'NK-nTtGQ7Hd4LTgEJgpEPA5Mz': "ABC Maritime AG has informed OpenSanctions that it disputes this entry, which is reproduced from the Iran Business Registry published by United Against Nuclear Iran."
}

// the names of schemas we don't by default want to display alongside the other matchable ones
export const EXCLUDE_SCHEMA_NAMES = ['Address', 'Position'];

// In Next.js, process.cwd() reliably resolves to the project root (/site/)
// at both build time and runtime (unlike __dirname, which points to .next/server/).
// https://nextjs.org/docs/pages/api-reference/functions/get-static-props#reading-files-use-processcwd
export const SITE_ROOT = process.cwd();
